import React, { useCallback, useMemo, useState } from "react";
import EntityList from "../EntityList/EntityList";
import swal from "sweetalert";
import SwalTestEmailForm from "./SwalTestEmailForm";
import { createRoot } from "react-dom/client";
import { defineMessages, FormattedMessage, useIntl } from "react-intl";

import globalMessages from "../../intl/globalMessages";
import { logSafeError } from "../../../../lib/safeLog";
import { useConfig } from "../../../../lib/config";
import EmailConfiguration from "./EmailConfiguration";
import { usePropertyConfiguration } from "../../../../components/property-configuration-provider";
import { useChainData } from "../../../../components/chain-data-provider";
import { isAgrEmailTemplate } from "./domain/agr.helpers";
import { resolveTemplateType } from "./application/emailTemplateForm.helpers";
import {
  downloadTemplatesExcel,
  getAgrTemplates,
  deleteAgrTemplates,
} from "./infrastructure/emailTemplateAgr.api";
import { createExcelFileDownloadFromRawData } from "../../../../lib/supportFunctions";

const messages = defineMessages({
  ...globalMessages,
  sendTestEmail: {
    id: "app.emailTemplateList.sendTestEmail",
    description: "Send Test Email Prompt Title",
    defaultMessage: "Send test email",
  },
  sendTestEmailInstructions: {
    id: "app.emailTemplateList.sendTestEmailInstructions",
    description: "Send Test Email Prompt Instructions",
    defaultMessage: "Please provide an existing reservation id (PXXXXXXX) to populate email data.",
  },
  emailSentSuccess: {
    id: "app.emailTemplateList.emailSentSuccess",
    description: "Email sent message",
    defaultMessage: "Email successfully sent!",
  },
  emailSentFailed: {
    id: "app.emailTemplateList.emailSentFailed",
    description: "Email sent failed",
    defaultMessage: "Email could not be sent",
  },
  send: {
    id: "app.emailTemplateList.send",
    description: "Send action",
    defaultMessage: "Send",
  },
  ALL: {
    id: "app.emailTemplateList.destinationType.all",
    description: "Destination type",
    defaultMessage: "Todos",
  },
  GUEST: {
    id: "app.emailTemplateList.destinationType.guest",
    description: "Destination type",
    defaultMessage: "Huéspedes",
  },
  PROPERTY: {
    id: "app.emailTemplateList.destinationType.property",
    description: "Destination type",
    defaultMessage: "Establecimiento",
  },
  usageShared: {
    id: "app.emailTemplateList.usageShared",
    description: "Shared usage label",
    defaultMessage: "Shared",
  },
  usageOwn: {
    id: "app.emailTemplateList.usageOwn",
    description: "Own usage label",
    defaultMessage: "Own",
  },
  motorsMultiple: {
    id: "app.emailTemplateList.motorsMultiple",
    description: "Motors column multiple label",
    defaultMessage: "Multiple",
  },
  templateTypeCustom: {
    id: "app.emailTemplateList.templateTypeCustom",
    description: "Template type column: custom template",
    defaultMessage: "Custom",
  },
  templateTypeDefault: {
    id: "app.emailTemplateList.templateTypeDefault",
    description: "Template type column: default template",
    defaultMessage: "Default",
  },
});

function isTestEmailAvailable({ entity }) {
  return (
    entity.type === "RESERVATION_CONFIRMATION" ||
    entity.type === "POST_STAY" ||
    entity.type === "PRE_STAY"
  );
}

const EMPTY_ARRAY = [];

function EmailTemplateList(props) {
  const {
    config,
    success,
    entities,
    loading,
    entityError,
    entityResult,
    handleCreate,
    handleEdit,
    onDeleteListItem,
    onToggleListItem,
    sendAPIRequest,
    hotelTicker,
    isWitAdmin,
  } = props;

  const intl = useIntl();
  const globalConfig = useConfig();
  const propertyConfig = usePropertyConfiguration();
  const chainData = useChainData();
  const isChain = !!chainData?.isChain;
  const [isDownloadingExcel, setIsDownloadingExcel] = useState(false);

  const { name, entityName, validation, fields, renderers, restConfig } = config;

  const showAdvancedEmailsManager = useMemo(
    () => propertyConfig && propertyConfig.showAdvancedEmailsManager === "1",
    [propertyConfig]
  );

  const handleDelete = useCallback(
    async ({ event, entity }) => {
      event.preventDefault();
      event.stopPropagation();
      if (isChain) {
        // Aggregator parent: delete through the aggregator endpoint so the backend cascades the
        // removal to every child copy. onDeleteListItem's generic delete only hits the individual
        // endpoint, which removes the parent and orphans the children. Reload to refetch the
        // cached legacy list (mirrors the create/update AGR flow).
        const baseURL = (restConfig && restConfig.baseURL) || "";
        try {
          await deleteAgrTemplates(baseURL, hotelTicker, entity.id);
          window.location.reload();
          return undefined;
        } catch (error) {
          logSafeError("Error deleting AGR email template", error);
          // Return an error-shaped result so EntityList shows its error alert; returning a plain
          // value makes EntityList report the failed delete as "Deleted successfully".
          return { response: { status: error?.response?.status ?? 500 } };
        }
      }
      return onDeleteListItem({ id: entity.id, entity });
    },
    [isChain, onDeleteListItem, restConfig, hotelTicker]
  );

  const handleToggle = useCallback(
    ({ event, entity }) => {
      if (event) {
        event.preventDefault();
        event.stopPropagation();
      }
      // Aggregator-shared rows are governed from the aggregator engine — their active state can't
      // be toggled from a child.
      if (isAgrEmailTemplate(entity)) {
        return;
      }
      // onToggleListItem PUTs the whole row entity, but the list mapper adds display-only fields
      // (uso, motors, allowDelete) to it. Strip them so the toggle PUT carries only the real DTO,
      // matching the create/edit save (those extras corrupted the update before).
      const cleanEntity = { ...entity };
      delete cleanEntity.uso;
      delete cleanEntity.motors;
      delete cleanEntity.allowDelete;
      delete cleanEntity.propertyTicker;
      onToggleListItem({
        id: cleanEntity.id,
        entity: cleanEntity,
        activeProperty: "active",
        requestObjectActiveProperty: "isActive",
      });
    },
    [onToggleListItem]
  );

  const displaySuccessAlert = useCallback(() => {
    swal(
      intl.formatMessage(messages.success),
      intl.formatMessage(messages.emailSentSuccess),
      "success"
    );
  }, [intl]);

  const displayErrorAlert = useCallback(() => {
    swal(
      intl.formatMessage(messages.failed),
      intl.formatMessage(messages.emailSentFailed),
      "error"
    );
  }, [intl]);

  // In aggregator mode we resolve the child copies (their ticker + emailTemplateId)
  // so the test email can target the selected child engine (B1-4).
  const resolveChildTargets = useCallback(
    async entity => {
      if (!isChain || !entity.id) {
        return EMPTY_ARRAY;
      }
      try {
        const baseURL = (restConfig && restConfig.baseURL) || "";
        const map = await getAgrTemplates(baseURL, hotelTicker, entity.id);
        return Object.keys(map)
          .filter(ticker => ticker !== hotelTicker)
          .map(ticker => ({ ticker, emailTemplateId: map[ticker]?.id }));
      } catch (error) {
        logSafeError("Error resolving AGR child templates for test email", error);
        return EMPTY_ARRAY;
      }
    },
    [isChain, hotelTicker, restConfig]
  );

  // Sends the test email. In aggregator mode `target` carries the selected child
  // engine ticker + child copy id; in individual mode it falls back to the entity itself.
  const sendTestEmail = useCallback(
    async ({ entity, formValues, target }) => {
      const { destinationEmail, reservationId } = formValues || {};
      if (!destinationEmail || !reservationId) {
        return false;
      }
      const hotelTickerToUse = target?.ticker || entity.hotelTicker;
      const emailTemplateId = target?.emailTemplateId ?? entity.id;
      try {
        await sendAPIRequest({
          options: {
            url: `${globalConfig.engineApiEndpoint}/api/emails/reservations`,
            method: "post",
            // Scope the send to the target engine. The shared auth interceptor does NOT add
            // x-property for /api/emails URLs, so set it explicitly (mirrors the aggregator API
            // calls) — needed so a test can target a child engine from the aggregator.
            headers: { "x-property": hotelTickerToUse },
            data: {
              hotelTicker: hotelTickerToUse,
              roomStayId: reservationId,
              type: entity.type,
              emailDestinationType: entity.emailDestinationType,
              ignoreDefaultRecipients: true,
              recipients: destinationEmail.split(","),
              emailTemplateId,
            },
          },
          reloadEntities: false,
          ignoreState: true,
        });
        return true;
      } catch (error) {
        logSafeError("Send test email failed", error);
        return false;
      }
    },
    [sendAPIRequest, globalConfig.engineApiEndpoint]
  );

  const handlePromptSendTestEmail = useCallback(
    async ({ entity }) => {
      const childTargets = await resolveChildTargets(entity);
      const formState = { destinationEmail: "", reservationId: "", motor: childTargets[0]?.ticker };

      const wrapper = document.createElement("div");
      const root = createRoot(wrapper);
      root.render(
        <SwalTestEmailForm
          destinationEmail={formState.destinationEmail}
          reservationId={formState.reservationId}
          motor={formState.motor}
          motors={childTargets.map(t => t.ticker)}
          isAggregator={isChain}
          onChange={({ formValues }) => Object.assign(formState, formValues)}
          intl={intl}
        />
      );

      const value = await swal({
        title: intl.formatMessage(messages.sendTestEmail),
        text: intl.formatMessage(messages.sendTestEmailInstructions),
        icon: "warning",
        content: wrapper,
        buttons: {
          cancel: intl.formatMessage(messages.cancel),
          confirm: {
            text: intl.formatMessage(messages.send),
            closeModal: false,
            value: true,
          },
        },
        dangerMode: true,
      });

      if (value) {
        const target = isChain
          ? childTargets.find(t => t.ticker === formState.motor)
          : undefined;
        const result = await sendTestEmail({ entity, formValues: formState, target });
        if (result) {
          displaySuccessAlert();
        } else {
          displayErrorAlert();
        }
      }
      return false;
    },
    [intl, isChain, resolveChildTargets, sendTestEmail, displaySuccessAlert, displayErrorAlert]
  );

  // A shared template is opened with the standard Edit action: everything is locked except
  // `preferred` (handled in EmailTemplateForm). A separate read-only "View" was dropped — it
  // would be identical to Edit apart from disabling `preferred`, so Edit alone suffices (per
  // Product 2026-08-17). Delete is still hidden for shared rows via `allowDelete`.
  const customActions = useMemo(
    () => [
      {
        icon: "fa fa-envelope",
        tooltipId: "send-custom",
        tooltipText: intl.formatMessage(messages.sendTestEmail),
        onExecute: handlePromptSendTestEmail,
        isAvailable: isTestEmailAvailable,
      },
    ],
    [intl, handlePromptSendTestEmail]
  );

  const emailDestinationTypeCell = useCallback(({ field, entity }) => {
    const fieldName = field && field.fieldName;
    const value = entity && entity[fieldName];
    return <FormattedMessage {...messages[value]} />;
  }, []);

  // Preferred column: green check when preferred, grey cross otherwise.
  const preferredCell = useCallback(
    ({ entity }) =>
      entity && entity.preferred ? (
        <em className="fa fa-check" style={{ color: "#2e7d32" }} aria-label="preferred" />
      ) : (
        <em className="fa fa-times" style={{ color: "#c0c4c8" }} aria-label="not preferred" />
      ),
    []
  );

  // Template type column (Custom/Default): translated via intl (like the other cells) instead of
  // a hardcoded English string, derived from the translations (single source with the form).
  const templateTypeCell = useCallback(
    ({ entity }) =>
      intl.formatMessage(
        resolveTemplateType(entity) === "CUSTOM"
          ? messages.templateTypeCustom
          : messages.templateTypeDefault
      ),
    [intl]
  );

  const listRenderers = useMemo(
    () => ({
      ...renderers,
      emailDestinationType: emailDestinationTypeCell,
      preferred: preferredCell,
      templateType: templateTypeCell,
    }),
    [renderers, emailDestinationTypeCell, preferredCell, templateTypeCell]
  );

  // Column gating by mode:
  // - `motors` renders only in aggregator (chain) mode.
  // - `uso` renders only in individual (child) mode (US-004): origin (Shared/Own).
  // - `templateType` + `preferred` render in BOTH modes.
  const computedFields = useMemo(() => {
    const listFields = (fields.list || []).filter(field => {
      if (field.fieldName === "motors") {
        return isChain;
      }
      if (field.fieldName === "uso") {
        return !isChain;
      }
      return true;
    });
    return { ...fields, list: listFields };
  }, [fields, isChain]);

  const customEntityToListValuesMapper = useCallback(
    ({ entities: entityList }) =>
      entityList.map(oldEntity => {
        const entity = { ...oldEntity };
        const isShared = isAgrEmailTemplate(entity);
        // Usage column (individual mode): "Shared" for aggregator rows, "Own" for local ones.
        entity.uso = isShared
          ? intl.formatMessage(messages.usageShared)
          : intl.formatMessage(messages.usageOwn);

        const childTickers = Array.isArray(entity.childPropertyTickers)
          ? entity.childPropertyTickers
          : [];
        if (childTickers.length === 1) {
          entity.motors = childTickers[0];
        } else if (childTickers.length > 1) {
          entity.motors = intl.formatMessage(messages.motorsMultiple);
        } else {
          entity.motors = "";
        }

        entity.allowDelete = !isShared;
        return entity;
      }),
    [intl]
  );

  const handleDownloadExcel = useCallback(async () => {
    setIsDownloadingExcel(true);
    try {
      const baseURL = (restConfig && restConfig.baseURL) || "";
      const result = await downloadTemplatesExcel(baseURL, hotelTicker);
      createExcelFileDownloadFromRawData(result.data, result.filename);
    } catch (error) {
      logSafeError("Error downloading email templates excel", error);
    } finally {
      setIsDownloadingExcel(false);
    }
  }, [hotelTicker, restConfig]);

  return (
    <>
      {showAdvancedEmailsManager && (
        <div className="content-wrapper">
          <div className="content-heading">Emails</div>
          <EmailConfiguration isWitAdmin={isWitAdmin} isAggregator={isChain} />
        </div>
      )}
      <EntityList
        loading={loading}
        entityName={entityName}
        entities={entities}
        validation={validation}
        renderers={listRenderers}
        name={name}
        fields={computedFields}
        onDelete={handleDelete}
        onToggle={handleToggle}
        entityError={entityError}
        entityResult={entityResult}
        handleCreate={handleCreate}
        handleEdit={handleEdit}
        customActions={customActions}
        customEntityToListValuesMapper={customEntityToListValuesMapper}
        isDownloadingExcel={isDownloadingExcel}
        onDownloadExcel={isChain ? handleDownloadExcel : undefined}
        success={success}
      />
    </>
  );
}

EmailTemplateList.defaultProps = {
  entities: [],
};

export default EmailTemplateList;
